// src/tabs/BanhoSubTab.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Zap, Scale, Layers, Clock, ShieldCheck, Compass, HelpCircle, Save, Plus, Trash2, Edit3 } from "lucide-react";
import { pb } from "../services/pocketbase";

export default function BanhoSubTab({ user }) {
  const [metals, setMetals] = useState([]);
  const [pieceTypes, setPieceTypes] = useState([]);
  
  const [selectedMetalId, setSelectedMetalId] = useState("");
  const [selectedPieceTypeId, setSelectedPieceTypeId] = useState("");
  const [pesoPeca, setPesoPeca] = useState("10"); // g
  const [espessuraDesejada, setEspessuraDesejada] = useState("3"); // µm
  const [eficiencia, setEficiencia] = useState("95"); // %

  // Eficiência Real check states
  const [pesoAntes, setPesoAntes] = useState("");
  const [pesoDepois, setPesoDepois] = useState("");
  const [tempoRealSegundos, setTempoRealSegundos] = useState("");
  const [correnteReal, setCorrenteReal] = useState("");
  const [eficienciaMedida, setEficienciaMedida] = useState(null);

  // Metal CRUD states
  const [isMetalModalOpen, setIsMetalModalOpen] = useState(false);
  const [editingMetalId, setEditingMetalId] = useState(null);
  const [metalName, setMetalName] = useState("");
  const [metalSymbol, setMetalSymbol] = useState("");
  const [metalDensidade, setMetalDensidade] = useState("");
  const [metalEqEletroq, setMetalEqEletroq] = useState("");
  const [metalEficienciaPadrao, setMetalEficienciaPadrao] = useState("");
  const [metalDensidadeCorrente, setMetalDensidadeCorrente] = useState("");
  const [metalEspessuraMin, setMetalEspessuraMin] = useState("");
  const [metalEspessuraPadrao, setMetalEspessuraPadrao] = useState("");
  const [metalEspessuraMax, setMetalEspessuraMax] = useState("");
  const [metalObs, setMetalObs] = useState("");

  // Piece Type CRUD states
  const [isPieceModalOpen, setIsPieceModalOpen] = useState(false);
  const [editingPieceId, setEditingPieceId] = useState(null);
  const [pieceName, setPieceName] = useState("");
  const [pieceFatorMin, setPieceFatorMin] = useState("");
  const [pieceFatorMax, setPieceFatorMax] = useState("");

  // Load seeds
  const loadInitialData = useCallback(async () => {
    try {
      const [metalRecords, pieceRecords] = await Promise.all([
        pb.collection("banho_metais").getFullList({ sort: "nome" }),
        pb.collection("banho_tipos_peca").getFullList({ sort: "nome" })
      ]);
      
      setMetals(metalRecords);
      setPieceTypes(pieceRecords);

      if (metalRecords.length > 0) setSelectedMetalId(metalRecords[0].id);
      if (pieceRecords.length > 0) setSelectedPieceTypeId(pieceRecords[0].id);
    } catch (e) {
      console.error("Erro loading plating seeds:", e);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Selected config
  const activeMetal = useMemo(() => metals.find(m => m.id === selectedMetalId), [selectedMetalId, metals]);
  const activePiece = useMemo(() => pieceTypes.find(p => p.id === selectedPieceTypeId), [selectedPieceTypeId, pieceTypes]);

  // Pre-fill fields when metal changes
  useEffect(() => {
    if (activeMetal) {
      setEspessuraDesejada(activeMetal.espessura_padrao?.toString() || "3");
      setEficiencia(activeMetal.eficiencia_padrao?.toString() || "95");
    }
  }, [activeMetal]);

  // Perform Calculations (Faraday's Law)
  const results = useMemo(() => {
    if (!activeMetal || !activePiece) return null;

    const peso = parseFloat(pesoPeca) || 0;
    const thickness = parseFloat(espessuraDesejada) || 0;
    const eff = (parseFloat(eficiencia) || 95) / 100;

    // Fator área
    const fatorMin = activePiece.fator_area_min || 15;
    const fatorMax = activePiece.fator_area_max || 20;
    const fatorMedio = (fatorMin + fatorMax) / 2;

    // 1. Área superficial
    const areaCm2 = peso * fatorMedio;
    const areaDm2 = areaCm2 / 100;

    // 2. Volume depositado
    const thicknessCm = thickness / 10000;
    const volumeCm3 = areaCm2 * thicknessCm;

    // 3. Massa de metal depositada
    const densidade = activeMetal.densidade || 19.3;
    const massaG = volumeCm3 * densidade;

    // 4. Corrente elétrica (A)
    const densCorrente = activeMetal.densidade_corrente || 1.5;
    const correnteA = areaDm2 * densCorrente;

    // 5. Tempo (Lei de Faraday)
    // tempo_horas = massa / (corrente * eq_eletroq * eficiencia)
    const eqEletroq = activeMetal.equivalente_eletroquimico || 0.122;
    let tempoHoras = 0;
    if (correnteA > 0 && eqEletroq > 0 && eff > 0) {
      tempoHoras = massaG / (correnteA * eqEletroq * eff);
    }
    const tempoMinutos = tempoHoras * 60;
    const tempoSegundos = tempoMinutos * 60;

    // 6. Consumo real de metal
    const consumoG = eff > 0 ? (massaG / eff) : 0;

    return {
      areaCm2: Math.round(areaCm2 * 100) / 100,
      areaDm2: Math.round(areaDm2 * 1000) / 1000,
      massaG: Math.round(massaG * 1000) / 1000,
      correnteA: Math.round(correnteA * 100) / 100,
      tempoMinutos: Math.round(tempoMinutos * 10) / 10,
      tempoSegundos: Math.round(tempoSegundos),
      consumoG: Math.round(consumoG * 1000) / 1000,
      tempoTexto: `${Math.floor(tempoMinutos)}m ${Math.round(tempoSegundos % 60)}s`
    };
  }, [activeMetal, activePiece, pesoPeca, espessuraDesejada, eficiencia]);

  // Real Efficiency Calculation
  const handleCalculateRealEfficiency = (e) => {
    e.preventDefault();
    if (!activeMetal) return;

    const antes = parseFloat(pesoAntes);
    const depois = parseFloat(pesoDepois);
    const tempoSecs = parseFloat(tempoRealSegundos);
    const corrReal = parseFloat(correnteReal);

    if (isNaN(antes) || isNaN(depois) || isNaN(tempoSecs) || isNaN(corrReal)) {
      alert("Por favor, preencha todos os campos reais com valores válidos.");
      return;
    }

    const massaReal = depois - antes;
    if (massaReal <= 0) {
      alert("O peso final deve ser maior que o peso inicial.");
      return;
    }

    const tempoHoras = (tempoSecs / 3600);
    const eqEletroq = activeMetal.equivalente_eletroquimico || 0.122;

    // eficiencia_real = (massaReal / (corrente * tempo * eq_eletroq)) * 100
    let effMedida = 0;
    const divisor = corrReal * tempoHoras * eqEletroq;
    if (divisor > 0) {
      effMedida = (massaReal / divisor) * 100;
    }

    setEficienciaMedida(Math.round(effMedida * 10) / 10);
  };

  const handleSaveHistory = async () => {
    if (!activeMetal || !activePiece || !results) return;
    try {
      await pb.collection("calculos_banho").create({
        metal_id: activeMetal.id,
        metal_nome: activeMetal.nome,
        tipo_peca_nome: activePiece.nome,
        peso_peca: parseFloat(pesoPeca) || 0,
        espessura_desejada: parseFloat(espessuraDesejada) || 0,
        resultado: {
          area_superficial: results.areaCm2,
          volume_depositado: results.massaG / activeMetal.densidade,
          massa_metal: results.massaG,
          corrente_eletrica: results.correnteA,
          tempo_minutos: results.tempoMinutos,
          tempo_horas: results.tempoMinutos / 60,
          consumo_metal: results.consumoG,
          eficiencia_real: eficienciaMedida !== null ? eficienciaMedida : activeMetal.eficiencia_padrao
        }
      });
      alert("Cálculo de galvanoplastia salvo no histórico!");
    } catch (e) {
      alert("Erro ao salvar histórico: " + e.message);
    }
  };

  // Metal CRUD handlers
  const handleOpenNewMetal = () => {
    setEditingMetalId(null);
    setMetalName("");
    setMetalSymbol("");
    setMetalDensidade("19.3");
    setMetalEqEletroq("0.122");
    setMetalEficienciaPadrao("95");
    setMetalDensidadeCorrente("1.5");
    setMetalEspessuraMin("1");
    setMetalEspessuraPadrao("3");
    setMetalEspessuraMax("10");
    setMetalObs("");
    setIsMetalModalOpen(true);
  };

  const handleOpenEditMetal = () => {
    if (!activeMetal) return;
    setEditingMetalId(activeMetal.id);
    setMetalName(activeMetal.nome || "");
    setMetalSymbol(activeMetal.simbolo || "");
    setMetalDensidade(activeMetal.densidade?.toString() || "");
    setMetalEqEletroq(activeMetal.equivalente_eletroquimico?.toString() || "");
    setMetalEficienciaPadrao(activeMetal.eficiencia_padrao?.toString() || "");
    setMetalDensidadeCorrente(activeMetal.densidade_corrente?.toString() || "");
    setMetalEspessuraMin(activeMetal.espessura_minima?.toString() || "");
    setMetalEspessuraPadrao(activeMetal.espessura_padrao?.toString() || "");
    setMetalEspessuraMax(activeMetal.espessura_maxima?.toString() || "");
    setMetalObs(activeMetal.observacoes || "");
    setIsMetalModalOpen(true);
  };

  const handleSaveMetal = async (e) => {
    e.preventDefault();
    if (!metalName.trim()) return alert("Nome do metal é obrigatório.");

    const payload = {
      nome: metalName.trim().toUpperCase(),
      simbolo: metalSymbol.trim(),
      densidade: parseFloat(metalDensidade) || 0,
      equivalente_eletroquimico: parseFloat(metalEqEletroq) || 0,
      eficiencia_padrao: parseFloat(metalEficienciaPadrao) || 0,
      densidade_corrente: parseFloat(metalDensidadeCorrente) || 0,
      espessura_minima: parseFloat(metalEspessuraMin) || 0,
      espessura_padrao: parseFloat(metalEspessuraPadrao) || 0,
      espessura_maxima: parseFloat(metalEspessuraMax) || 0,
      observacoes: metalObs.trim(),
      tipo: editingMetalId ? activeMetal.tipo : "customizado"
    };

    try {
      let record;
      if (editingMetalId) {
        record = await pb.collection("banho_metais").update(editingMetalId, payload);
        setMetals(prev => prev.map(m => m.id === editingMetalId ? record : m));
        alert("Metal atualizado com sucesso!");
      } else {
        record = await pb.collection("banho_metais").create(payload);
        setMetals(prev => [...prev, record].sort((a,b) => a.nome.localeCompare(b.nome)));
        setSelectedMetalId(record.id);
        alert("Metal cadastrado com sucesso!");
      }
      setIsMetalModalOpen(false);
    } catch (err) {
      alert("Erro ao salvar metal: " + err.message);
    }
  };

  const handleDeleteMetal = async () => {
    if (!selectedMetalId) return;
    const metal = metals.find(m => m.id === selectedMetalId);
    if (!metal) return;
    if (!window.confirm(`Deseja realmente excluir o metal "${metal.nome}"?`)) return;

    try {
      await pb.collection("banho_metais").delete(metal.id);
      const remaining = metals.filter(m => m.id !== metal.id);
      setMetals(remaining);
      if (remaining.length > 0) {
        setSelectedMetalId(remaining[0].id);
      } else {
        setSelectedMetalId("");
      }
      alert("Metal excluído com sucesso!");
    } catch (err) {
      alert("Erro ao excluir metal: " + err.message);
    }
  };

  // Piece Type CRUD handlers
  const handleOpenNewPiece = () => {
    setEditingPieceId(null);
    setPieceName("");
    setPieceFatorMin("15");
    setPieceFatorMax("25");
    setIsPieceModalOpen(true);
  };

  const handleOpenEditPiece = () => {
    if (!activePiece) return;
    setEditingPieceId(activePiece.id);
    setPieceName(activePiece.nome || "");
    setPieceFatorMin(activePiece.fator_area_min?.toString() || "");
    setPieceFatorMax(activePiece.fator_area_max?.toString() || "");
    setIsPieceModalOpen(true);
  };

  const handleSavePiece = async (e) => {
    e.preventDefault();
    if (!pieceName.trim()) return alert("Nome do tipo de peça é obrigatório.");

    const payload = {
      nome: pieceName.trim(),
      fator_area_min: parseFloat(pieceFatorMin) || 0,
      fator_area_max: parseFloat(pieceFatorMax) || 0
    };

    try {
      let record;
      if (editingPieceId) {
        record = await pb.collection("banho_tipos_peca").update(editingPieceId, payload);
        setPieceTypes(prev => prev.map(p => p.id === editingPieceId ? record : p));
        alert("Tipo de peça atualizado!");
      } else {
        record = await pb.collection("banho_tipos_peca").create(payload);
        setPieceTypes(prev => [...prev, record].sort((a,b) => a.nome.localeCompare(b.nome)));
        setSelectedPieceTypeId(record.id);
        alert("Tipo de peça cadastrado!");
      }
      setIsPieceModalOpen(false);
    } catch (err) {
      alert("Erro ao salvar tipo de peça: " + err.message);
    }
  };

  const handleDeletePiece = async () => {
    if (!selectedPieceTypeId) return;
    const piece = pieceTypes.find(p => p.id === selectedPieceTypeId);
    if (!piece) return;
    if (!window.confirm(`Deseja realmente excluir o tipo de peça "${piece.nome}"?`)) return;

    try {
      await pb.collection("banho_tipos_peca").delete(piece.id);
      const remaining = pieceTypes.filter(p => p.id !== piece.id);
      setPieceTypes(remaining);
      if (remaining.length > 0) {
        setSelectedPieceTypeId(remaining[0].id);
      } else {
        setSelectedPieceTypeId("");
      }
      alert("Tipo de peça excluído com sucesso!");
    } catch (err) {
      alert("Erro ao excluir tipo de peça: " + err.message);
    }
  };

  return (
    <div className="flex-1 flex flex-col md:flex-row h-full overflow-hidden bg-slate-50">
      
      {/* LEFT: CALCULATOR INPUTS */}
      <div className="flex-1 p-6 overflow-y-auto custom-scrollbar space-y-6">
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Zap className="text-amber-500 fill-amber-500" /> Calculadora de Banho (Faraday)
              </h2>
              <p className="text-xs text-slate-500">Cálculo eletroquímico preciso para espessura de deposição metálica</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex justify-between items-center">
                <span>Metal do Banho</span>
              </label>
              <div className="flex gap-2">
                <select
                  value={selectedMetalId}
                  onChange={(e) => setSelectedMetalId(e.target.value)}
                  className="flex-1 p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-slate-800 font-bold bg-white cursor-pointer"
                >
                  {metals.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.nome} ({m.simbolo})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleOpenNewMetal}
                  className="p-2.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl border border-blue-200 transition-colors flex items-center justify-center shrink-0"
                  title="Novo Metal"
                >
                  <Plus size={18} />
                </button>
                {selectedMetalId && (
                  <>
                    <button
                      type="button"
                      onClick={handleOpenEditMetal}
                      className="p-2.5 bg-amber-50 text-amber-600 hover:bg-amber-100 rounded-xl border border-amber-200 transition-colors flex items-center justify-center shrink-0"
                      title="Editar Metal"
                    >
                      <Edit3 size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteMetal}
                      className="p-2.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl border border-red-200 transition-colors flex items-center justify-center shrink-0"
                      title="Excluir Metal"
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Escolha Tipo Peça */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex justify-between items-center">
                <span>Tipo de Peça (Área Superficial)</span>
              </label>
              <div className="flex gap-2">
                <select
                  value={selectedPieceTypeId}
                  onChange={(e) => setSelectedPieceTypeId(e.target.value)}
                  className="flex-1 p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 outline-none text-slate-800 font-bold bg-white cursor-pointer"
                >
                  {pieceTypes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome} (aprox. {((p.fator_area_min + p.fator_area_max)/2).toFixed(0)} cm²/g)
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleOpenNewPiece}
                  className="p-2.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-xl border border-blue-200 transition-colors flex items-center justify-center shrink-0"
                  title="Novo Tipo de Peça"
                >
                  <Plus size={18} />
                </button>
                {selectedPieceTypeId && (
                  <>
                    <button
                      type="button"
                      onClick={handleOpenEditPiece}
                      className="p-2.5 bg-amber-50 text-amber-600 hover:bg-amber-100 rounded-xl border border-amber-200 transition-colors flex items-center justify-center shrink-0"
                      title="Editar Tipo de Peça"
                    >
                      <Edit3 size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={handleDeletePiece}
                      className="p-2.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl border border-red-200 transition-colors flex items-center justify-center shrink-0"
                      title="Excluir Tipo de Peça"
                    >
                      <Trash2 size={18} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Peso Peca - Slider & Input */}
            <div className="md:col-span-2 space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Peso Total das Peças (g)
                </label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="0.1"
                    step="any"
                    value={pesoPeca}
                    onChange={(e) => setPesoPeca(e.target.value)}
                    className="w-20 p-1 border border-slate-300 rounded-lg text-center font-bold font-mono text-sm"
                  />
                  <span className="text-xs font-bold text-slate-400">g</span>
                </div>
              </div>
              <input
                type="range"
                min="0.5"
                max="250"
                step="0.5"
                value={pesoPeca}
                onChange={(e) => setPesoPeca(e.target.value)}
                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
              />
            </div>

            {/* Espessura Desejada - Slider & Input */}
            <div className="md:col-span-2 space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Espessura da Camada Metálica (µm)
                </label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={espessuraDesejada}
                    onChange={(e) => setEspessuraDesejada(e.target.value)}
                    className="w-20 p-1 border border-slate-300 rounded-lg text-center font-bold font-mono text-sm"
                  />
                  <span className="text-xs font-bold text-slate-400">µm</span>
                </div>
              </div>
              <input
                type="range"
                min={activeMetal?.espessura_minima || 0.1}
                max={activeMetal?.espessura_maxima || 20}
                step="0.1"
                value={espessuraDesejada}
                onChange={(e) => setEspessuraDesejada(e.target.value)}
                className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
              />
              {activeMetal && (
                <div className="flex justify-between text-[10px] text-slate-400 font-bold px-1">
                  <span>Mín: {activeMetal.espessura_minima}µm</span>
                  <span>Sugestão: {activeMetal.espessura_padrao}µm</span>
                  <span>Máx: {activeMetal.espessura_maxima}µm</span>
                </div>
              )}
            </div>

            {/* Eficiência Estimada */}
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                Eficiência do Processo (%)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={eficiencia}
                  onChange={(e) => setEficiencia(e.target.value)}
                  className="w-full p-2.5 border border-slate-300 rounded-xl outline-none focus:ring-2 focus:ring-amber-500 font-mono font-bold"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-slate-400">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* RESULTS CARD GRID */}
        {results && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 text-center text-white">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Área Superficial</span>
              <span className="text-xl font-extrabold font-mono text-amber-400">{results.areaCm2} cm²</span>
              <span className="text-[9px] text-slate-400 block mt-0.5">({results.areaDm2} dm²)</span>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 text-center text-white">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Corrente Ideal</span>
              <span className="text-xl font-extrabold font-mono text-amber-400">{results.correnteA} A</span>
              <span className="text-[9px] text-slate-400 block mt-0.5">({activeMetal?.densidade_corrente} A/dm²)</span>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 text-center text-white">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Tempo Estimado</span>
              <span className="text-xl font-extrabold font-mono text-amber-400">{results.tempoTexto}</span>
              <span className="text-[9px] text-slate-400 block mt-0.5">({results.tempoSegundos} segundos)</span>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 text-center text-white">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Consumo Real Metal</span>
              <span className="text-xl font-extrabold font-mono text-amber-400">{results.consumoG} g</span>
              <span className="text-[9px] text-slate-400 block mt-0.5">(depósito: {results.massaG}g)</span>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleSaveHistory}
            className="flex-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 font-bold p-3.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 text-sm"
          >
            <Save size={18} /> Salvar Configuração no Histórico
          </button>
        </div>
      </div>

      {/* RIGHT: REAL EFFICIENCY AUDITING METER */}
      <div className="w-full md:w-96 border-t md:border-t-0 md:border-l border-slate-200 bg-white flex flex-col shrink-0 p-6 overflow-y-auto custom-scrollbar">
        <h3 className="font-bold text-slate-700 flex items-center gap-1.5 text-sm mb-4 border-b border-slate-100 pb-3 shrink-0">
          <Compass size={16} className="text-amber-500" /> Auditoria: Eficiência Real
        </h3>

        <form onSubmit={handleCalculateRealEfficiency} className="space-y-4 flex-1">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex gap-2 text-amber-800 text-xs">
            <HelpCircle size={20} className="shrink-0 mt-0.5" />
            <p>Pese a gancheira/peça na balança analítica de precisão antes e logo após o banho para medir a eficiência exata.</p>
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              Peso Antes do Banho (g)
            </label>
            <input
              type="number"
              step="any"
              required
              value={pesoAntes}
              onChange={(e) => setPesoAntes(e.target.value)}
              placeholder="Ex: 12.352"
              className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-amber-500 font-mono font-bold text-xs"
            />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              Peso Depois do Banho (g)
            </label>
            <input
              type="number"
              step="any"
              required
              value={pesoDepois}
              onChange={(e) => setPesoDepois(e.target.value)}
              placeholder="Ex: 12.684"
              className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-amber-500 font-mono font-bold text-xs"
            />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              Corrente Aplicada Real (A)
            </label>
            <input
              type="number"
              step="any"
              required
              value={correnteReal}
              onChange={(e) => setCorrenteReal(e.target.value)}
              placeholder="Ex: 0.45"
              className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-amber-500 font-mono font-bold text-xs"
            />
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              Tempo Real de Cuba (segundos)
            </label>
            <input
              type="number"
              required
              value={tempoRealSegundos}
              onChange={(e) => setTempoRealSegundos(e.target.value)}
              placeholder="Ex: 240"
              className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-amber-500 font-mono font-bold text-xs"
            />
          </div>

          <button
            type="submit"
            className="w-full bg-slate-800 hover:bg-slate-900 text-white font-bold p-3 rounded-lg text-xs transition-all shadow-sm"
          >
            Calcular Eficiência Real
          </button>
        </form>

        {eficienciaMedida !== null && (
          <div className="mt-6 border-t border-slate-100 pt-4 text-center shrink-0">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Eficiência Real Medida</span>
            <span className={`text-4xl font-black font-mono ${eficienciaMedida > 90 ? "text-emerald-600" : "text-amber-600"}`}>
              {eficienciaMedida}%
            </span>
            <div className="flex gap-1 justify-center mt-2">
              <button
                onClick={() => {
                  setEficiencia(eficienciaMedida.toString());
                  setEficienciaMedida(null);
                }}
                className="text-[10px] font-bold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full border border-emerald-200"
              >
                Injetar no Calculador
              </button>
            </div>
          </div>
        )}
      </div>

      {/* METAL MODAL */}
      {isMetalModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up max-h-[90vh] overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">
                {editingMetalId ? "Editar Metal do Banho" : "Cadastrar Novo Metal"}
              </h3>
              <button onClick={() => setIsMetalModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleSaveMetal} className="p-6 space-y-4 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Metal *</label>
                  <input
                    type="text"
                    required
                    value={metalName}
                    onChange={(e) => setMetalName(e.target.value)}
                    placeholder="Ex: OURO AMARELO 18K"
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 font-bold text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Símbolo Químico</label>
                  <input
                    type="text"
                    value={metalSymbol}
                    onChange={(e) => setMetalSymbol(e.target.value)}
                    placeholder="Ex: Au"
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Densidade (g/cm³)</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={metalDensidade}
                    onChange={(e) => setMetalDensidade(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" title="Equivalente eletroquímico em g/Ah">Eq. Eletroquímico</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={metalEqEletroq}
                    onChange={(e) => setMetalEqEletroq(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Eficiência Padrão (%)</label>
                  <input
                    type="number"
                    required
                    value={metalEficienciaPadrao}
                    onChange={(e) => setMetalEficienciaPadrao(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Dens. Corrente (A/dm²)</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={metalDensidadeCorrente}
                    onChange={(e) => setMetalDensidadeCorrente(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Espessura Mín. (µm)</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={metalEspessuraMin}
                    onChange={(e) => setMetalEspessuraMin(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Espessura Sugerida</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={metalEspessuraPadrao}
                    onChange={(e) => setMetalEspessuraPadrao(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Espessura Máx. (µm)</label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={metalEspessuraMax}
                    onChange={(e) => setMetalEspessuraMax(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Observações</label>
                  <textarea
                    value={metalObs}
                    onChange={(e) => setMetalObs(e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs h-14"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsMetalModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PIECE TYPE MODAL */}
      {isPieceModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">
                {editingPieceId ? "Editar Tipo de Peça" : "Cadastrar Tipo de Peça"}
              </h3>
              <button onClick={() => setIsPieceModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleSavePiece} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Tipo *</label>
                <input
                  type="text"
                  required
                  value={pieceName}
                  onChange={(e) => setPieceName(e.target.value)}
                  placeholder="Ex: Anel Liso"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 font-bold text-xs"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Fator Área Mín (cm²/g)</label>
                  <input
                    type="number"
                    required
                    value={pieceFatorMin}
                    onChange={(e) => setPieceFatorMin(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Fator Área Máx (cm²/g)</label>
                  <input
                    type="number"
                    required
                    value={pieceFatorMax}
                    onChange={(e) => setPieceFatorMax(e.target.value)}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-amber-500 text-xs"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsPieceModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
